import { useEffect, useState } from "react";
import { LifeBuoy, AlertCircle, Mail, Check } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { useToast } from "@/components/ui/Toast";
import { useShell } from "@/components/Shell";
import {
  listTickets,
  updateSettings,
  type Ticket,
  type TicketFilter,
} from "@/lib/api";
import { cn } from "@/lib/cn";
import { TicketRow } from "./TicketRow";

const FILTERS: { key: TicketFilter; label: string }[] = [
  { key: "unresolved", label: "Needs attention" },
  { key: "all", label: "All" },
  { key: "resolved", label: "Resolved" },
  { key: "closed", label: "Closed" },
];

/**
 * Tickets page. Campers raise support messages from the chat widget (a
 * complaint, a reservation question, a general doubt). Each lands here and
 * — if a notification email is set — in the owner's inbox. The owner triages
 * status, jots private notes, and can reply to the camper by email.
 *
 * The notification-email setting lives right here (not buried in Settings)
 * because it's the one knob that makes this whole page useful.
 */
export function Tickets() {
  const toast = useToast();
  const { home, refresh } = useShell();
  const [filter, setFilter] = useState<TicketFilter>("unresolved");
  const [tickets, setTickets] = useState<Ticket[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async (f: TicketFilter) => {
    setLoading(true);
    setError(null);
    try {
      const r = await listTickets({ status: f });
      setTickets(r.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(filter); }, [filter]);

  const onUpdated = (updated: Ticket) => {
    setTickets((prev) => {
      if (!prev) return prev;
      // Drop from the list if it no longer matches the active filter.
      const stillMatches =
        filter === "all" ||
        (filter === "unresolved"
          ? updated.status === "open" || updated.status === "in_progress"
          : updated.status === filter);
      return stillMatches
        ? prev.map((t) => (t.id === updated.id ? updated : t))
        : prev.filter((t) => t.id !== updated.id);
    });
    // Keep the sidebar badge + home counts honest.
    void refresh();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-medium text-text-strong">Tickets</h1>
        <p className="mt-1 text-sm text-text-soft">
          Messages campers send from the chat widget — reservation questions, complaints, anything the bot couldn't handle. Reply here and they'll get an email.
        </p>
      </div>

      <NotificationEmailCard
        current={home?.campground.notification_email ?? null}
        onSaved={async () => { await refresh(); toast.push("Notification email saved."); }}
      />

      <div className="flex flex-wrap items-center gap-1.5">
        {FILTERS.map((f) => (
          <FilterPill key={f.key} active={filter === f.key} onClick={() => setFilter(f.key)}>
            {f.label}
          </FilterPill>
        ))}
      </div>

      {error && (
        <Card className="border-danger/30">
          <div className="flex items-start gap-3 px-5 py-4">
            <AlertCircle size={18} strokeWidth={1.75} className="mt-0.5 shrink-0 text-danger" />
            <div className="flex-1">
              <p className="text-sm font-medium text-text-strong">Couldn't load tickets</p>
              <p className="mt-1 text-xs text-text-soft">{error}</p>
            </div>
          </div>
        </Card>
      )}

      {!error && loading && !tickets && <ListSkeleton />}

      {!error && tickets && tickets.length === 0 && (
        <Card>
          <div className="px-5 py-6">
            <EmptyState
              icon={LifeBuoy}
              title={filter === "unresolved" ? "Nothing needs attention" : "No tickets here"}
              description="When a camper sends a message from your chat widget, it shows up here."
            />
          </div>
        </Card>
      )}

      {!error && tickets && tickets.length > 0 && (
        <div className="space-y-3">
          {tickets.map((t) => (
            <TicketRow key={t.id} ticket={t} onUpdated={onUpdated} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Inline editor for campgrounds.notification_email. */
function NotificationEmailCard({
  current,
  onSaved,
}: {
  current: string | null;
  onSaved: () => Promise<void>;
}) {
  const toast = useToast();
  const [value, setValue] = useState(current ?? "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValue(current ?? ""); }, [current]);

  const dirty = value.trim() !== (current ?? "");

  const save = async () => {
    setSaving(true);
    try {
      await updateSettings({ notification_email: value.trim() });
      await onSaved();
    } catch (e) {
      toast.push(e instanceof Error ? e.message : "Couldn't save", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-end sm:justify-between">
        <label className="block flex-1">
          <span className="flex items-center gap-1.5 text-xs font-medium text-text-strong">
            <Mail size={13} strokeWidth={1.75} /> Notify me at
          </span>
          <input
            type="email"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="you@campground.com"
            disabled={saving}
            className="mt-1.5 block w-full rounded border border-line bg-surface px-3 py-2 text-sm text-text-strong outline-none focus:border-coral sm:max-w-sm"
          />
          <span className="mt-1 block text-2xs text-text-soft">
            {current
              ? "New tickets are emailed here. Clear the field to turn notifications off."
              : "Add an address to get emailed the moment a camper writes in. Tickets still show up here either way."}
          </span>
        </label>
        <Button
          variant="primary"
          size="sm"
          onClick={() => void save()}
          loading={saving}
          disabled={!dirty || saving}
          iconLeft={!saving && <Check size={12} strokeWidth={1.75} />}
        >
          Save
        </Button>
      </div>
    </Card>
  );
}

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-1 text-xs transition-colors",
        active
          ? "border-coral bg-coral-soft text-coral-ink"
          : "border-line bg-surface text-text-soft hover:bg-surface-2 hover:text-text-strong",
      )}
    >
      {children}
    </button>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="card">
          <div className="flex items-start justify-between gap-4 px-5 py-4">
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-3 w-1/4" />
            </div>
            <Skeleton className="h-6 w-16" />
          </div>
        </div>
      ))}
    </div>
  );
}
